import { css } from 'aphrodite';
import { observer } from 'mobx-react';
import * as React from 'react';
import { useEffect, useState } from 'react';
import { FilterOperator } from '../../../../extViewmodels';
import { Checkbox } from '../../../../web/components-v2/Checkbox';
import { Label } from '../../../../web/components-v2/Label';
import { ISelectOption } from '../../../../web/components/DeprecatedSelect';
import { TagListBuilder } from '../../../../web/components/entities/tags/TagListBuilder';
import { FieldKey } from '../../../viewModels/form';
import { LeadRuleViewModel } from '../../../viewModels/leadrules';
import { filterByChecked, reduceChildren } from '../../leadView/components/OutcomeSelect/utils';
import { styleSheet } from '../styles';
import { useTagList } from '../utils';
import { DrawerSection } from './DrawerSection';
import { OutcomeChecklist } from './OutcomeChecklist';

interface IProps {
	leadRule: LeadRuleViewModel;
}

export const EditFilters: React.FC<IProps> = observer(({ leadRule }) => {
	const [orTags, addOrTag, removeOrTag, setOrTags] = useTagList([], false);
	const [andTags, addAndTag, removeAndTag, setAndTags] = useTagList([], false);
	const [notTags, addNotTag, removeNotTag, setNotTags] = useTagList([], false);
	const [outcomes, setOutcomes] = useState([]);

	const [showingAndTags, setShowingAndTags] = useState(false);
	const [showingNotTags, setShowingNotTags] = useState(false);
	const [showingOutcomesSelect, setShowingOutcomesSelect] = useState(false);

	useEffect(() => {
		if (leadRule) {
			const tags = leadRule?.filterToArrays;
			setOrTags(tags.or);
			setAndTags(tags.and);
			setNotTags(tags.not);

			setOutcomes(tags.outcomes);
			setShowingAndTags(!!tags.and.length);
			setShowingNotTags(!!tags.not.length);
			setShowingOutcomesSelect(!!tags.outcomes.length);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [leadRule]);

	useEffect(() => {
		leadRule?.setFilterFromArrays(andTags, orTags, notTags, outcomes);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [orTags, andTags, notTags, outcomes]);

	const handleNotCheckChange = (checked: boolean) => {
		setShowingNotTags(checked);
		setNotTags([]);
	};

	const handleAndCheckChange = (checked: boolean) => {
		setShowingAndTags(checked);
		setAndTags([]);
	};

	const handleOutcomesCheckChange = (checked: boolean) => {
		setShowingOutcomesSelect(checked);
		setOutcomes([]);
	};

	const handleOutcomesChange = (selectedOutcome: ISelectOption<FieldKey>[]) => {
		setOutcomes(
			selectedOutcome
				?.reduce(reduceChildren, [])
				?.filter(filterByChecked)
				?.map((i: ISelectOption<FieldKey>) => i.name)
		);
	};

	return (
		<DrawerSection title='Tags'>
			<p className={css(styleSheet.subLabel)}>Included tag(s):</p>
			<TagListBuilder
				styles={[styleSheet.tagBuilder]}
				inputId='add-or-tags'
				onTagAdded={addOrTag}
				onTagRemoved={removeOrTag}
				separator={FilterOperator.Or}
				tags={orTags}
				openOnFocus
			/>
			<p className={css(styleSheet.subLabel)}>Narrow the list:</p>
			<Label htmlFor='and-tags'>
				<Checkbox id='and-tags' checked={showingAndTags} onCheckedChange={handleAndCheckChange} />
				Contacts must also have these tags...
			</Label>
			{showingAndTags ? (
				<TagListBuilder
					styles={[styleSheet.tagBuilder]}
					inputId='add-and-tags'
					onTagAdded={addAndTag}
					onTagRemoved={removeAndTag}
					separator={FilterOperator.And}
					tags={andTags}
					openOnFocus
				/>
			) : null}
			<Label htmlFor='not-tags'>
				<Checkbox id='not-tags' checked={showingNotTags} onCheckedChange={handleNotCheckChange} />
				Exclude these tags...
			</Label>
			{showingNotTags ? (
				<TagListBuilder
					styles={[styleSheet.tagBuilder]}
					inputId='add-not-tags'
					onTagAdded={addNotTag}
					onTagRemoved={removeNotTag}
					separator={FilterOperator.Not}
					tags={notTags}
					openOnFocus
				/>
			) : null}
			<Label htmlFor='outcomes'>
				<Checkbox id='outcomes' checked={showingOutcomesSelect} onCheckedChange={handleOutcomesCheckChange} />
				Only include the following outcomes...
			</Label>
			{showingOutcomesSelect ? (
				<OutcomeChecklist key={leadRule?.id ?? 'new'} defaultValues={outcomes} onChange={handleOutcomesChange} />
			) : null}
		</DrawerSection>
	);
});
